"use client";

import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Auswahl } from "./formular/auswahl";

/*
 * Reiter (Design-Guide `reiter.md`, M-006; im Paket seit M-011): Inhalte EINES Objekts in gleichrangige Ansichten teilen,
 * Zähler hinter dem Namen. Zwei Varianten: `unterstrichen` (Hauptreiter, Preline bottom-border-tabs) und `segment`
 * (Segment-Reiter, Preline segmented-tabs; beide aus card-tabs). Die Preline-JS-Hülle (hs-tab) wird nicht benutzt: der
 * Zustand liegt in React, damit Tastatur (Pfeile, Pos1, Ende) und ARIA (tablist/tab/tabpanel, aria-selected, aria-controls,
 * roving tabindex) sicher stimmen. Nicht gewählte Panels bleiben im Markup (hidden). `abstand="flach"` (M-011) setzt die
 * unterstrichene Leiste ohne Kartenrand in einen schon gepolsterten Bereich (Detail-Hauptspalte).
 */
export interface ReiterEintrag {
  id: string;
  label: string;
  /** Zähler hinter dem Namen; nur gerendert, wenn die Zahl bekannt ist. */
  zaehler?: number;
  /**
   * Kurzer Zustandstext hinter dem Namen („läuft“, „4 Punkte“, „0 von 4 Ergebnissen“), klein und gedämpft; steht im Namen des Reiters (Hilfstechnik liest „Durchführen läuft“),
   * nur wenn es etwas zu sagen gibt (Besprechung-Seite neu, L3, Betreiber 19.10.2026; additiv, Abweichung AW-082 (3)/(5) neu gefasst, Design-Guide reiter.md).
   */
  zusatz?: string;
  inhalt: ReactNode;
  /** Der Reiter ist ohne Recht nicht nutzbar: sichtbar, gesperrt. */
  gesperrt?: boolean;
  /** Symbol vor dem Namen (Lucide, `aria-hidden`); nur Schmuck, der Name trägt die Bedeutung (Aufgaben-Detail A4′). */
  symbol?: ReactNode;
}

const TAB_UNTERSTRICHEN_AKTIV =
  "px-2 py-1.5 mb-2 relative inline-flex justify-center items-center gap-x-2 text-foreground text-sm rounded-lg focus:outline-hidden focus-visible:bg-muted-focus after:absolute after:-bottom-2 after:inset-x-2 after:z-10 after:h-0.5 after:pointer-events-none after:bg-primary whitespace-nowrap";
const TAB_UNTERSTRICHEN_AKTIV_FETT = TAB_UNTERSTRICHEN_AKTIV.replace("text-sm ", "text-sm font-semibold ");
const TAB_UNTERSTRICHEN_INAKTIV =
  "px-2 py-1.5 mb-2 relative inline-flex justify-center items-center gap-x-2 hover:bg-muted-hover text-muted-foreground-1 hover:text-foreground text-sm rounded-lg focus:outline-hidden focus-visible:bg-muted-focus after:absolute after:-bottom-2 after:inset-x-2 after:z-10 after:h-0.5 after:pointer-events-none whitespace-nowrap";
const TAB_SEGMENT_AKTIV =
  "bg-layer shadow-sm py-2 px-3 inline-flex justify-center items-center gap-x-2 text-sm text-foreground rounded-full focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus whitespace-nowrap";
const TAB_SEGMENT_INAKTIV =
  "py-2 px-3 inline-flex justify-center items-center gap-x-2 text-sm text-foreground rounded-full focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-focus whitespace-nowrap";
const ZAEHLER = "inline-flex items-center py-0.5 px-1.5 rounded-full text-xs font-medium bg-surface text-surface-foreground";
const ZAEHLER_KOMPAKT = "inline-flex items-center py-0.5 px-1 2xl:px-1.5 rounded-full text-xs font-medium bg-surface text-surface-foreground";

/**
 * Scroll-Hinweis (PM-A6 F10): läuft die Leiste unter der Breite quer über, blendet der Rand, hinter dem noch Reiter
 * liegen, aus (Tailwind-Maske, keine Farbe, daher auf jedem Untergrund richtig). Ohne Überlauf keine Maske. Der Zustand
 * folgt Scrollen und Größenänderung.
 */
export function useScrollHinweis() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [rand, setRand] = useState<{ links: boolean; rechts: boolean }>({ links: false, rechts: false });
  const messe = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const links = el.scrollLeft > 1;
    const rechts = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setRand((alt) => (alt.links === links && alt.rechts === rechts ? alt : { links, rechts }));
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    messe();
    el.addEventListener("scroll", messe, { passive: true });
    const beobachter = typeof ResizeObserver === "function" ? new ResizeObserver(messe) : null;
    beobachter?.observe(el);
    // Ändert sich nur der Inhalt (mehr Spalten, Zähler), nicht der Rahmen, ändert sich die Überlauf-Lage trotzdem.
    if (el.firstElementChild) beobachter?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", messe);
      beobachter?.disconnect();
    };
  }, [messe]);
  const maske: Maske = rand.links && rand.rechts ? "beide" : rand.rechts ? "rechts" : rand.links ? "links" : "keine";
  return { ref, maske };
}

export type Maske = "keine" | "rechts" | "links" | "beide";
/** Feste, vollständige Klassenketten je Maske (nie zusammengesetzt). */
const LEISTE_SEGMENT: Record<Maske, string> = {
  keine: "overflow-x-auto",
  rechts: "overflow-x-auto mask-r-from-90%",
  links: "overflow-x-auto mask-l-from-90%",
  beide: "overflow-x-auto mask-x-from-90%",
};
const LEISTE_FLACH: Record<Maske, string> = {
  keine: "border-b border-line-2 overflow-x-auto",
  rechts: "border-b border-line-2 overflow-x-auto mask-r-from-90%",
  links: "border-b border-line-2 overflow-x-auto mask-l-from-90%",
  beide: "border-b border-line-2 overflow-x-auto mask-x-from-90%",
};
const LEISTE_KARTE: Record<Maske, string> = {
  keine: "px-5 pt-3 border-b border-line-2 overflow-x-auto",
  rechts: "px-5 pt-3 border-b border-line-2 overflow-x-auto mask-r-from-90%",
  links: "px-5 pt-3 border-b border-line-2 overflow-x-auto mask-l-from-90%",
  beide: "px-5 pt-3 border-b border-line-2 overflow-x-auto mask-x-from-90%",
};

/** Feste Klassen je Umschaltpunkt (nie zusammengesetzt): unter ihm steht ein Auswahlfeld, ab ihm die Reiterleiste (Variante `unterstrichen`). */
const AUSWAHL_UNTER: Record<"md" | "lg", { auswahl: string; leiste: string }> = {
  md: { auswahl: "md:hidden", leiste: "max-md:hidden" },
  lg: { auswahl: "lg:hidden", leiste: "max-lg:hidden" },
};

/**
 * Zustand und Tastatur der Reiter (tablist/tab/tabpanel): EINE Quelle für `Reiter` und `PhasenLeiste`. Roving tabindex, Pfeile, Pos1, Ende, `aria-controls`; gewählter Reiter
 * fehlt = der erste; gesteuert (`aktiv`) oder selbst gehalten (`start`). Ein gesperrter Eintrag (`gesperrt`) lässt sich nie wählen: Klick und Pfeiltasten wählen ihn nicht,
 * die Pfeiltasten stellen aber den Fokus auf ihn (er ist sichtbar und trägt seinen Grund; nie eine Taste, die an ihm hängen bleibt).
 */
export function useReiterSteuerung({
  eintraege,
  start,
  aktiv: aktivVorgabe,
  beimWechsel,
}: {
  eintraege: ReadonlyArray<{ id: string; gesperrt?: boolean }>;
  start?: string;
  aktiv?: string;
  beimWechsel?: (id: string, durchTastatur: boolean) => void;
}) {
  const basis = useId();
  const [aktivIntern, setAktiv] = useState(start ?? eintraege[0]?.id ?? "");
  const gewaehlt = aktivVorgabe ?? aktivIntern;
  // Fehlt der gewählte Reiter (z. B. ausgeblendet, weil sich der Zustand des Objekts geändert hat), gilt der erste: nie ein leeres Panel ohne gewählten Reiter.
  const aktiv = eintraege.some((e) => e.id === gewaehlt) ? gewaehlt : (eintraege[0]?.id ?? "");
  const knoepfe = useRef(new Map<string, HTMLButtonElement>());
  const tabId = (id: string) => `${basis}-tab-${id}`;
  const panelId = (id: string) => `${basis}-panel-${id}`;
  const knopfRef = (id: string) => (el: HTMLButtonElement | null) => {
    if (el) knoepfe.current.set(id, el);
    else knoepfe.current.delete(id);
  };

  const waehle = (id: string, fokus: boolean) => {
    if (eintraege.find((e) => e.id === id)?.gesperrt) return;
    setAktiv(id);
    beimWechsel?.(id, fokus);
    if (fokus) knoepfe.current.get(id)?.focus();
    // Der gewählte Reiter rückt in den sichtbaren Bereich der quer laufenden Leiste (jsdom kennt es nicht).
    knoepfe.current.get(id)?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  };

  const taste = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const n = eintraege.length;
    let ziel: number | null = null;
    if (e.key === "ArrowRight") ziel = (index + 1) % n;
    else if (e.key === "ArrowLeft") ziel = (index - 1 + n) % n;
    else if (e.key === "Home") ziel = 0;
    else if (e.key === "End") ziel = n - 1;
    if (ziel === null) return;
    e.preventDefault();
    const eintrag = eintraege[ziel]!;
    // Gesperrt: nur den Fokus setzen (wählen darf man ihn nicht), damit die Pfeiltaste nicht an ihm hängen bleibt.
    if (eintrag.gesperrt) knoepfe.current.get(eintrag.id)?.focus();
    else waehle(eintrag.id, true);
  };

  return { aktiv, knoepfe, tabId, panelId, knopfRef, waehle, taste };
}

export function Reiter({
  beschriftung,
  eintraege,
  variante = "unterstrichen",
  abstand = "karte",
  start,
  aktiv: aktivVorgabe,
  beimWechsel,
  leistenZusatz,
  panelAbstand = "standard",
  kompaktMobil = false,
  aktivFett = false,
  auswahlUnter,
  segmentSchmal = false,
}: {
  /**
   * Nur `unterstrichen` ohne `leistenZusatz` (Muster M-172/M-185, Betreiber 03.11.2026): unter dem Umschaltpunkt (`md` bzw. `lg`) steht
   * statt der Leiste ein natives Auswahlfeld mit denselben Einträgen (Zähler in Klammern), ab dem Punkt die Leiste; Panels, Zustand
   * und Wahl sind dieselben. Standard aus (die Leiste scrollt quer). Für Leisten mit vielen Reitern (CMDB-Register: neun Sichten).
   */
  auswahlUnter?: "md" | "lg";
  /**
   * Nur `segment` (additiv, Betreiber 03.11.2026 „N1“, Abweichung AW-162): Polsterung der Reiter `px-2.5` statt `px-3`, damit das Segment
   * „Physisch · Services · Alle“ in der schmalen Modul-Seitenleiste der CMDB vollständig im Bild steht. Standard aus.
   */
  segmentSchmal?: boolean;
  /**
   * Nur `unterstrichen` (additiv, entspricht der Preline-Vorschau (M-122)): der gewählte Reiter ist zusätzlich
   * zur Unterstreichung fett (`font-semibold`), damit er auch ohne Farbe erkennbar ist. Standard aus: alle anderen Verwender bleiben unverändert.
   */
  aktivFett?: boolean;
  /**
   * Nur `unterstrichen`: unter `sm` ohne Symbole und mit engerem Innenabstand, damit die Reiterleiste auf dem Telefon vollständig im Bild steht
   * (Aufgaben-Detail, Abnahme 15.10.2026: „Verlauf“ war abgeschnitten); was trotzdem überläuft, scrollt waagerecht mit Rand-Verlauf.
   */
  kompaktMobil?: boolean;
  beschriftung: string;
  eintraege: ReiterEintrag[];
  variante?: "unterstrichen" | "segment";
  /** `flachEingerueckt`: wie `flach`, die Namen sitzen zusätzlich 8 px eingerückt (auf der 16-px-Linie der Kartentitel darunter). */
  abstand?: "karte" | "flach" | "flachEingerueckt";
  /**
   * Nur `unterstrichen`: Element rechts neben den Reitern in derselben Kopfzeile (Zahnrad im Benachrichtigungsmenü, M-068,
   * Block notifications-menu); der Zusatz steht außerhalb der quer scrollenden Leiste. Abweichung AW-085 (4).
   */
  leistenZusatz?: ReactNode;
  /** `keiner`: die Panels tragen keinen eigenen Rand, der Inhalt sitzt bündig unter der Leiste (Listen im Aufklappmenü). */
  panelAbstand?: "standard" | "keiner";
  /** Id des anfangs gewählten Reiters (Standard: der erste). */
  start?: string;
  /**
   * Gesteuerter Betrieb (Aufgaben-Detail A4′: die Wahl steht in der Adresse und kann von außen wechseln, z. B. „Nachfragen“ springt
   * auf das Gespräch): der Aufrufer hält den gewählten Reiter, der Baustein meldet Wechsel nur über `beimWechsel`. Fehlt es, hält der
   * Baustein den Zustand selbst (`start`).
   */
  aktiv?: string;
  /** `durchTastatur`: der Wechsel kam von den Pfeil-, Pos1- oder Ende-Tasten (der Fokus folgt dem Reiter), sonst per Klick. */
  beimWechsel?: (id: string, durchTastatur: boolean) => void;
}) {
  const { aktiv, knoepfe, tabId, panelId, knopfRef, waehle, taste } = useReiterSteuerung({
    eintraege,
    ...(start !== undefined ? { start } : {}),
    ...(aktivVorgabe !== undefined ? { aktiv: aktivVorgabe } : {}),
    ...(beimWechsel ? { beimWechsel } : {}),
  });
  const { ref: scrollRef, maske } = useScrollHinweis();
  const auswahlId = useId();
  // Der gewählte Reiter liegt immer im sichtbaren Teil der quer laufenden Leiste, auch beim ersten Bild (`start`) und bei einem
  // Wechsel von außen (nicht nur nach Klick oder Taste). Nur waagerecht, die Seite springt nicht (PM einfach P1, Brett mobil).
  useEffect(() => {
    const leiste = scrollRef.current;
    const knopf = knoepfe.current.get(aktiv);
    if (!leiste || !knopf) return;
    const k = knopf.getBoundingClientRect();
    const l = leiste.getBoundingClientRect();
    if (k.left < l.left) leiste.scrollLeft -= l.left - k.left + 8;
    else if (k.right > l.right) leiste.scrollLeft += k.right - l.right + 8;
  }, [aktiv, scrollRef]);
  const segment = variante === "segment";
  const knopf = (e: ReiterEintrag, i: number) => (
    <button
      key={e.id}
      ref={knopfRef(e.id)}
      type="button"
      role="tab"
      id={tabId(e.id)}
      aria-selected={aktiv === e.id}
      aria-controls={panelId(e.id)}
      aria-disabled={e.gesperrt || undefined}
      tabIndex={aktiv === e.id ? 0 : -1}
      className={
        segment
          ? (aktiv === e.id ? TAB_SEGMENT_AKTIV : TAB_SEGMENT_INAKTIV).replace("px-3", segmentSchmal ? "px-2.5" : "px-3")
          : (aktiv === e.id ? (aktivFett ? TAB_UNTERSTRICHEN_AKTIV_FETT : TAB_UNTERSTRICHEN_AKTIV) : TAB_UNTERSTRICHEN_INAKTIV).replace(
              "px-2 ",
              // Kompakt bis unter 2xl (1536 px): bei 1280 px steht die Aufgaben-Leiste mit fünf Reitern sonst nicht ganz im Bild.
              kompaktMobil ? "px-1.5 2xl:px-2 " : "px-2 ",
            ).replace("gap-x-2 ", kompaktMobil ? "gap-x-1.5 2xl:gap-x-2 " : "gap-x-2 ")
      }
      onClick={() => waehle(e.id, false)}
      onKeyDown={(ev) => taste(ev, i)}
    >
      {e.symbol && (
        <span className={kompaktMobil && !segment ? "shrink-0 hidden sm:inline-flex" : "shrink-0 inline-flex"} aria-hidden="true">
          {e.symbol}
        </span>
      )}
      {e.label}
      {e.zusatz && (
        <>
          {" "}
          <span className="text-xs text-muted-foreground-1">{e.zusatz}</span>
        </>
      )}
      {typeof e.zaehler === "number" &&<span className={kompaktMobil && !segment ? ZAEHLER_KOMPAKT : ZAEHLER}>{e.zaehler}</span>}
    </button>
  );

  return (
    <div>
      {segment ? (
        <div ref={scrollRef} className={LEISTE_SEGMENT[maske]}>
          {/* Preline: navigation-and-layout/navigation-and-layout-sub-headers-navigation/navigation-and-layout-cat-card-tabs/segmented-tabs */}
          <div className="inline-flex gap-1 p-1 relative bg-surface rounded-full" role="tablist" aria-label={beschriftung} aria-orientation="horizontal">
            {eintraege.map(knopf)}
          </div>
        </div>
      ) : leistenZusatz ? (
        <div className={abstand === "flach" ? "flex items-center justify-between gap-x-2 border-b border-line-2" : "flex items-center justify-between gap-x-2 px-5 pt-3 border-b border-line-2"}>
          <div ref={scrollRef} className={LEISTE_SEGMENT[maske]}>
            {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-dropdown-menus/notifications-menu (Reiter und Zusatz in einer Kopfzeile) */}
            <div className="flex gap-1" role="tablist" aria-label={beschriftung} aria-orientation="horizontal">
              {eintraege.map(knopf)}
            </div>
          </div>
          <div className="shrink-0 mb-2">{leistenZusatz}</div>
        </div>
      ) : (
        <>
        {auswahlUnter && (
          <div className={AUSWAHL_UNTER[auswahlUnter].auswahl}>
            <Auswahl
              id={auswahlId}
              label={beschriftung}
              labelVersteckt
              groesse="kompakt"
              value={aktiv}
              onChange={(ev) => waehle(ev.target.value, false)}
              optionen={eintraege.map((e) => ({
                wert: e.id,
                label: typeof e.zaehler === "number" ? `${e.label} (${e.zaehler})` : e.label,
                ...(e.gesperrt ? { deaktiviert: true } : {}),
              }))}
            />
          </div>
        )}
        {(() => {
          const leiste = (
            <div ref={scrollRef} className={abstand === "flach" || abstand === "flachEingerueckt" ? LEISTE_FLACH[maske] : LEISTE_KARTE[maske]}>
              {/* Preline: navigation-and-layout/navigation-and-layout-sub-headers-navigation/navigation-and-layout-cat-card-tabs/bottom-border-tabs */}
              <div className={(kompaktMobil ? "flex gap-0.5 2xl:gap-1" : "flex gap-1") + (abstand === "flachEingerueckt" ? " px-2" : "")} role="tablist" aria-label={beschriftung} aria-orientation="horizontal">
                {eintraege.map(knopf)}
              </div>
            </div>
          );
          return auswahlUnter ? <div className={AUSWAHL_UNTER[auswahlUnter].leiste}>{leiste}</div> : leiste;
        })()}
        </>
      )}
      {eintraege.map((e) => (
        <div
          key={e.id}
          role="tabpanel"
          id={panelId(e.id)}
          aria-labelledby={tabId(e.id)}
          tabIndex={0}
          hidden={aktiv !== e.id}
          className={panelAbstand === "keiner" ? "focus:outline-hidden" : segment || abstand !== "karte" ? "pt-4 focus:outline-hidden" : "p-5 focus:outline-hidden"}
        >
          {e.inhalt}
        </div>
      ))}
    </div>
  );
}
